import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, MailX, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { API_URL } from '../lib/utils';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
    <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="unsubscribe-page">
      <div className="text-2xl font-bold text-[#2C9DE1] mb-8 tracking-tight">EMP Medic</div>
      {children}
    </div>
  </div>
);

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const id = params.get('id') || '';
  const t = params.get('t') || '';
  const [state, setState] = useState('loading');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) { setState('noid'); return; }
    axios.get(`${API_URL}/email-marketing/unsubscribe/info`, { params: { id, t } })
      .then(({ data }) => { setEmail(data.email); setState(data.already_unsubscribed ? 'done' : 'confirm'); })
      .catch(() => setState('invalid'));
  }, [id, t]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/email-marketing/unsubscribe`, { id, t, reason: reason || null });
      setState('done');
    } catch {
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (state === 'loading') {
    return <Shell><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></Shell>;
  }

  if (state === 'noid') {
    return (
      <Shell>
        <MailX className="w-10 h-10 text-[#2C9DE1] mb-4" />
        <h1 className="text-xl font-semibold text-slate-900 mb-2" data-testid="unsubscribe-noid">Διαγραφή από τη λίστα / Unsubscribe</h1>
        <p className="text-sm text-slate-600">
          Για να διαγραφείτε, πατήστε το κουμπί «Διαγραφή από τη λίστα» στο κάτω μέρος οποιουδήποτε email μας, ή στείλτε μας email στο <a className="underline" href="mailto:info@empmedic.com?subject=Unsubscribe">info@empmedic.com</a>.
          <br />To unsubscribe, use the "Unsubscribe" button at the bottom of any of our emails, or email <a className="underline" href="mailto:info@empmedic.com?subject=Unsubscribe">info@empmedic.com</a>.
        </p>
      </Shell>
    );
  }

  if (state === 'invalid' || state === 'error') {
    return (
      <Shell>
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
        <h1 className="text-xl font-semibold text-slate-900 mb-2" data-testid="unsubscribe-error">Μη έγκυρος σύνδεσμος / Invalid link</h1>
        <p className="text-sm text-slate-600">
          Παρακαλούμε στείλτε μας email στο <a className="underline" href="mailto:info@empmedic.com?subject=Unsubscribe">info@empmedic.com</a> και θα σας αφαιρέσουμε άμεσα.
          <br />Please email <a className="underline" href="mailto:info@empmedic.com?subject=Unsubscribe">info@empmedic.com</a> and we will remove you right away.
        </p>
      </Shell>
    );
  }

  if (state === 'done') {
    return (
      <Shell>
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-4" />
        <h1 className="text-xl font-semibold text-slate-900 mb-2" data-testid="unsubscribe-success">Διαγραφήκατε / You're unsubscribed</h1>
        <p className="text-sm text-slate-600 mb-4">
          Η διεύθυνση <strong>{email}</strong> δεν θα λαμβάνει πλέον προωθητικά emails από την EMP Medic.
          <br />The address <strong>{email}</strong> will no longer receive marketing emails from EMP Medic.
        </p>
        <p className="text-xs text-slate-500">
          Θα συνεχίσετε να λαμβάνετε μόνο απαραίτητα μηνύματα για τα πιστοποιητικά σας. / You will still receive essential messages about your certificates.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <MailX className="w-10 h-10 text-[#2C9DE1] mb-4" />
      <h1 className="text-xl font-semibold text-slate-900 mb-2">Διαγραφή από τη λίστα / Unsubscribe</h1>
      <p className="text-sm text-slate-600 mb-6">
        Να σταματήσουμε τα προωθητικά emails στη διεύθυνση <strong data-testid="unsubscribe-email">{email}</strong>;
        <br />Stop marketing emails to <strong>{email}</strong>?
      </p>
      <label className="text-xs font-medium text-slate-500 mb-1 block">Λόγος (προαιρετικό) / Reason (optional)</label>
      <Textarea
        data-testid="unsubscribe-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        maxLength={500}
        rows={3}
        className="mb-6"
      />
      <Button
        data-testid="unsubscribe-confirm-btn"
        onClick={confirm}
        disabled={submitting}
        className="w-full h-auto py-3 whitespace-normal leading-snug bg-[#2C9DE1] hover:bg-[#2386c4]"
      >
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Επιβεβαίωση διαγραφής / Confirm unsubscribe
      </Button>
    </Shell>
  );
}
